import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Clock, Printer, TrendingDown, TrendingUp, Users, Zap } from 'lucide-react';
import Layout from '../components/Layout';
import StatusBadge from '../components/StatusBadge';
import NudgeDialog from '../components/NudgeDialog';
import api, { formatError } from '../lib/api';
import { toast } from 'sonner';

const fmtDay = (d) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

export default function Snapshot() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [nudgeOpen, setNudgeOpen] = useState(false);
  const [nudgeRecipients, setNudgeRecipients] = useState([]);

  const openNudge = (people) => { setNudgeRecipients(people); setNudgeOpen(true); };

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/snapshot?days=${d}`);
      setData(data);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(days); }, [days, load]);

  const generatedLabel = useMemo(() => {
    if (!data) return '';
    return new Date(data.generated_at).toLocaleString();
  }, [data]);

  const windowLabel = useMemo(() => {
    if (!data) return '';
    const start = new Date(data.window_start);
    const end = new Date(data.generated_at);
    return `${fmtDay(start)} → ${fmtDay(end)}`;
  }, [data]);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto space-y-6 print:max-w-none">
        {/* Header */}
        <div className="flex items-end justify-between gap-4 flex-wrap print:hidden">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">BOSS DIGEST</div>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-none">SNAPSHOT</h1>
            <div className="text-sm text-zinc-600 mt-2 font-mono">
              {generatedLabel} · WINDOW: {windowLabel}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 border border-zinc-900">
              {[7, 14, 30].map((d) => (
                <button
                  key={d}
                  onClick={() => setDays(d)}
                  data-testid={`window-${d}`}
                  className={`px-3 py-2 text-[10px] font-bold uppercase tracking-[0.15em] ${days === d ? 'bg-zinc-900 text-white' : 'bg-white hover:bg-zinc-100'}`}
                >
                  {d}D
                </button>
              ))}
            </div>
            <button
              onClick={() => window.print()}
              data-testid="print-btn"
              className="flex items-center gap-2 border border-zinc-900 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.15em] hover:bg-zinc-900 hover:text-white transition-colors"
            >
              <Printer size={12} /> Print / Save PDF
            </button>
          </div>
        </div>

        {/* Print-only header */}
        <div className="hidden print:block border-b border-black pb-3 mb-3">
          <div className="font-display text-3xl font-black">SyncGrid Snapshot</div>
          <div className="text-sm font-mono">{generatedLabel} · Window: {windowLabel}</div>
        </div>

        {loading || !data ? (
          <div className="font-mono text-sm text-zinc-500">LOADING SNAPSHOT...</div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              <StatTile label="OPEN" value={data.stats.open} />
              <StatTile label="IN PROGRESS" value={data.stats.in_progress} bg="bg-[#FFD700]" />
              <StatTile label="RESOLVED" value={data.stats.resolved} bg="bg-black" text="text-white" />
              <StatTile label="UPDATES" value={data.stats.updates_in_window} sub={`LAST ${data.window_days}D`} />
              <StatTile label="OVERDUE" value={data.stats.overdue_count} bg={data.stats.overdue_count > 0 ? 'bg-red-600' : 'bg-white'} text={data.stats.overdue_count > 0 ? 'text-white' : 'text-zinc-900'} />
              <StatTile label="LAGGING" value={data.stats.lagging_count} bg={data.stats.lagging_count > 0 ? 'bg-red-600' : 'bg-white'} text={data.stats.lagging_count > 0 ? 'text-white' : 'text-zinc-900'} />
            </div>

            {/* People panel */}
            <section>
              <div className="flex items-center gap-2 mb-3 print:hidden">
                <Users size={14} strokeWidth={2} />
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">TEAM ACTIVITY</div>
                {data.stats.lagging_count > 0 && (
                  <button
                    onClick={() => openNudge(data.people.filter((p) => p.lagging))}
                    data-testid="nudge-all-lagging"
                    className="ml-auto flex items-center gap-1 bg-red-600 text-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] hover:bg-red-700 transition-colors"
                  >
                    <Zap size={12} /> Nudge all lagging ({data.stats.lagging_count})
                  </button>
                )}
              </div>
              <div className="bg-white border border-zinc-900">
                <div className="grid grid-cols-12 border-b border-zinc-900 bg-zinc-50 px-4 py-3 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-600">
                  <div className="col-span-4">Member</div>
                  <div className="col-span-2">Open Assigned</div>
                  <div className="col-span-2">Updates ({data.window_days}d)</div>
                  <div className="col-span-3">Last Update</div>
                  <div className="col-span-1 text-right">Status</div>
                </div>
                {data.people.map((p) => (
                  <div key={p.id} data-testid={`person-row-${p.id}`} className="grid grid-cols-12 border-b border-zinc-100 px-4 py-3 items-center text-sm">
                    <div className="col-span-4">
                      <div className="font-semibold">{p.name}</div>
                      <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">{p.role}</div>
                    </div>
                    <div className="col-span-2 font-mono">{p.assigned_open}</div>
                    <div className="col-span-2 font-mono flex items-center gap-1">
                      {p.updates_in_window}
                      {p.updates_in_window === 0 && p.assigned_open > 0 && <TrendingDown size={12} className="text-red-600" />}
                      {p.updates_in_window > 0 && <TrendingUp size={12} className="text-green-600" />}
                    </div>
                    <div className="col-span-3 text-xs text-zinc-600 font-mono">{p.last_update_at ? new Date(p.last_update_at).toLocaleString() : '—'}</div>
                    <div className="col-span-1 text-right">
                      <PersonStatusCell p={p} onNudge={openNudge} />
                    </div>
                  </div>
                ))}
                {data.people.length === 0 && <div className="p-6 text-sm text-zinc-500">No team members yet.</div>}
              </div>
            </section>

            {/* Open tasks */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Clock size={14} strokeWidth={2} />
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">OPEN TASKS & LATEST UPDATES · {data.open_tasks.length}</div>
              </div>
              {data.open_tasks.length === 0 ? (
                <div className="bg-white border border-zinc-200 p-8 text-center text-sm text-zinc-500">
                  Everything is resolved. Clean board.
                </div>
              ) : (
                <div className="space-y-2">
                  {data.open_tasks.map((t) => (
                    <Link
                      key={t.id}
                      to={`/tasks/${t.id}`}
                      data-testid={`snap-task-${t.id}`}
                      className="block bg-white border border-zinc-200 p-4 hover:border-black hover:shadow-brutal-sm transition-all"
                    >
                      <div className="flex items-start gap-3 flex-wrap">
                        <StatusBadge status={t.status} />
                        {t.is_overdue && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em] bg-red-600 text-white px-2 py-0.5 border border-red-600">
                            <AlertTriangle size={10} /> Overdue
                          </span>
                        )}
                        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-600">{t.priority}</span>
                        {t.due_date && <span className="text-[10px] font-mono text-zinc-500">DUE {t.due_date}</span>}
                        <span className="ml-auto text-[10px] font-mono text-zinc-500">
                          {t.days_since_update != null ? `${t.days_since_update}D STALE` : 'NEW'}
                        </span>
                      </div>
                      <div className="font-display font-bold text-lg leading-snug mt-2">{t.title}</div>
                      <div className="text-xs text-zinc-600 mt-1">
                        {t.is_general ? 'General · ' : ''}
                        {t.assignee_names.length > 0 && <>Assigned: <span className="font-semibold text-zinc-800">{t.assignee_names.join(', ')}</span></>}
                      </div>
                      {t.latest_update ? (
                        <div className="mt-3 border-l-2 border-zinc-900 pl-3 text-sm">
                          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
                            {t.latest_update.user_name} · {new Date(t.latest_update.created_at).toLocaleString()}
                          </div>
                          <div className="text-zinc-800 line-clamp-2 mt-1">{t.latest_update.message}</div>
                        </div>
                      ) : (
                        <div className="mt-3 border-l-2 border-red-600 pl-3 text-xs font-bold uppercase tracking-[0.15em] text-red-600">
                          No updates yet
                        </div>
                      )}
                    </Link>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </div>
      <NudgeDialog
        open={nudgeOpen}
        recipients={nudgeRecipients}
        onClose={() => setNudgeOpen(false)}
      />
    </Layout>
  );
}

function StatTile({ label, value, sub, bg = 'bg-white', text = 'text-zinc-900' }) {
  return (
    <div className={`${bg} ${text} border border-zinc-900 p-4`} data-testid={`snap-stat-${label.toLowerCase().replace(/ /g, '-')}`}>
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-80">{label}</div>
      <div className="font-display text-4xl font-black tracking-tighter leading-none mt-1">{value}</div>
      {sub && <div className="text-[9px] font-bold uppercase tracking-[0.15em] opacity-70 mt-1">{sub}</div>}
    </div>
  );
}

function PersonStatusCell({ p, onNudge }) {
  if (p.lagging) {
    return (
      <button
        onClick={() => onNudge([p])}
        data-testid={`nudge-person-${p.id}`}
        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em] bg-red-600 text-white px-2 py-0.5 border border-red-600 hover:bg-red-700 print:cursor-default"
        title="Send a quick nudge"
      >
        <Zap size={10} /> Nudge
      </button>
    );
  }
  if (p.assigned_open === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em] bg-zinc-100 text-zinc-600 px-2 py-0.5 border border-zinc-300">Clear</span>
    );
  }
  return (
    <button
      onClick={() => onNudge([p])}
      data-testid={`nudge-person-${p.id}`}
      className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em] bg-zinc-900 text-white px-2 py-0.5 border border-zinc-900 hover:bg-zinc-700 print:cursor-default"
      title="Send a quick nudge"
    >
      <CheckCircle2 size={10} /> Active
    </button>
  );
}
